/**
 * Quem pode entrar no painel: a mesma pergunta que o admin atual faz, com o mesmo critério.
 *
 * O admin atual (`portal/admin.html:3497`) lê a PRÓPRIA linha em `usuarios_sistema` pelo e-mail da
 * sessão. A policy `usuarios_sistema_self_check` deixa qualquer autenticado ler só a própria linha,
 * então a pergunta "sou usuário do painel?" funciona antes de se saber a resposta. Sem linha, ou
 * com falha na leitura, o admin atual mostra "Essa conta não tem acesso a este painel."
 * (`admin.html:3498`), e este módulo devolve `null` nos dois casos, pelo mesmo motivo.
 *
 * A LEITURA PASSA PELO PACOTE DE DADOS (ADR 0002): o app não importa o SDK do Supabase (trava do
 * lint do Chassi), e a consulta mora em `@studiolu/dados/areas/acesso`, com o RLS decidindo o que
 * volta, como antes.
 */

// A fonte de dados do pacote, a leitura da área de acesso e o desvio de mesma origem do dev.
import { criarFonteDeDados, fetchDeFuncoesNaMesmaOrigem, type FonteDeDados } from '@studiolu/dados';
import { lerAcessoPorEmail } from '@studiolu/dados/areas/acesso';
// URL e chave do projeto, e o provedor que guarda a sessão: os mesmos do login.
import { URL_SUPABASE, CHAVE_SUPABASE, provedor } from '../identidade.js';

// Os quatro papéis do painel e o rótulo de cada um, palavra por palavra (`admin.html:8813`).
export const PAPEL_LABEL: Readonly<Record<string, string>> = Object.freeze({
  administrador: 'Administrador',
  financeiro: 'Financeiro',
  marketing: 'Marketing',
  consulta: 'Apenas Consulta',
});

// O que a leitura devolve quando a pessoa tem acesso.
export interface AcessoAoPainel {
  readonly email: string;
  readonly papel: string;
}

/**
 * A fonte de dados ÚNICA do app, criada uma vez na carga do módulo, como o provedor.
 *
 * O token vem do provedor a cada requisição, e não de uma segunda sessão: o cliente do pacote é
 * criado com `accessToken` e não abre GoTrueClient próprio (ver `packages/dados/src/cliente.ts`).
 * O resto do painel usa ESTA fonte, e não cria outra.
 */
export const fonteDeDados: FonteDeDados = criarFonteDeDados({
  url: URL_SUPABASE,
  chave: CHAVE_SUPABASE,
  obterToken: async () => (await provedor.getSession())?.token ?? null,
  // Em desenvolvimento, a chamada de Edge Function sai pela mesma origem da página; ver
  // `desviarFuncoes` logo abaixo e `packages/dados/src/mesma-origem.ts`.
  ...(desviarFuncoes()
    ? {
        fetch: fetchDeFuncoesNaMesmaOrigem({
          urlDoProjeto: URL_SUPABASE,
          origemDaPagina: window.location.origin,
          // O `fetch` do navegador, preso ao `window`: solto, ele lança "Illegal invocation".
          fetchBase: window.fetch.bind(window),
        }),
      }
    : {}),
});

/**
 * Quando o desvio de mesma origem vale: só no servidor de desenvolvimento, e só em navegador de
 * gente.
 *
 * `import.meta.env.DEV` é falso no build, então nada disto existe no que o Render publica.
 *
 * `navigator.webdriver` é verdadeiro em navegador dirigido pelo Playwright, e ali o desvio FICA DE
 * FORA de propósito: cinco arquivos de apoio das suítes interceptam a chamada pelo endereço
 * absoluto do projeto (`${PROJETO}/functions/v1/**`, medido em 19/09/2026), e mudar a origem faria
 * a interceptação errar o alvo. O pior caso não seria o teste falhar: seria ele CHAMAR a função de
 * verdade achando que estava simulando.
 */
function desviarFuncoes(): boolean {
  // Fora do navegador (uma eventual execução em Node) não há origem nem `navigator`: não desvia.
  if (typeof window === 'undefined') return false;
  return import.meta.env.DEV && navigator.webdriver !== true;
}

/**
 * Lê a linha da pessoa em `usuarios_sistema`. `null` quando não há linha ou quando a leitura falha,
 * como no admin atual, que trata as duas coisas igual.
 *
 * O `_token` continua na assinatura porque `App.tsx` e `contexto.tsx` chamam assim. O token que vai
 * ao banco é o da mesma sessão, lido do provedor pela fonte, e por isso o parâmetro não é usado.
 */
export async function lerAcessoAoPainel(_token: string, email: string): Promise<AcessoAoPainel | null> {
  const resultado = await lerAcessoPorEmail(fonteDeDados, email);
  // Falha (recusa do banco, serviço ou rede) e ausência de linha dão o mesmo `null` do legado.
  return resultado.ok ? resultado.dados : null;
}
